import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ChevronRight, DollarSign, Clock, CheckCircle,
  AlertTriangle, Zap, Package, Users, Briefcase, Flag
} from 'lucide-react';
import { getPipelineOverview, getPipelineStage, movePipeline, assignPipelineItem } from '../services/api';
import { toast } from 'sonner';

const STAGES = [
  { id: 'opportunity', label: 'Opportunity', icon: Zap, color: 'bg-blue-500' },
  { id: 'under_review', label: 'Under Review', icon: Clock, color: 'bg-amber-500' },
  { id: 'approved', label: 'Approved', icon: CheckCircle, color: 'bg-emerald-500' },
  { id: 'executed', label: 'Executed', icon: Package, color: 'bg-purple-500' },
  { id: 'impact_realized', label: 'Impact Realized', icon: DollarSign, color: 'bg-green-600' }
];

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value?.toFixed(0) || 0}`;
}

function StageColumn({ stage, items, counts, values, onMove, onAssign }) {
  const StageIcon = stage.icon;
  
  return (
    <div className="flex-1 min-w-[250px]" data-testid={`stage-${stage.id}`}>
      <div className={`${stage.color} text-white p-4 rounded-t-xl`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StageIcon className="w-5 h-5" />
            <span className="font-semibold">{stage.label}</span>
          </div>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
            {counts || 0}
          </span>
        </div>
        <p className="text-sm text-white/70 mt-1">{formatCurrency(values || 0)} value</p>
      </div>
      
      <div className="bg-slate-100 p-3 space-y-2 min-h-[400px] rounded-b-xl">
        {items.map(item => (
          <div 
            key={item.id}
            className="bg-white rounded-lg p-3 shadow-sm border border-slate-200 group hover:shadow-md transition-shadow"
          >
            <Link to={`/drug/${item.reference_drug_id}`} className="block">
              <p className="font-medium text-slate-900 group-hover:text-[#0A2540] transition-colors">
                {item.name}
              </p>
              <p className="text-xs text-slate-500">{item.drug_brand_name || item.manufacturer}</p>
            </Link>
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-emerald-600 font-medium">
                {Math.round((item.estimated_savings_percent || 0) * 100)}% savings
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                item.confidence >= 85 ? 'bg-emerald-100 text-emerald-700' :
                item.confidence >= 70 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {item.confidence}%
              </span>
            </div>

            {/* Assignment & Priority badges */}
            {(item.assigned_to || item.priority) && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {item.assigned_to && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">
                    {item.assigned_to}
                  </span>
                )}
                {item.priority && item.priority !== 'medium' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    item.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-500'
                  }`}>
                    {item.priority}
                  </span>
                )}
              </div>
            )}
            
            {/* Quick assign actions - shown on under_review stage */}
            {stage.id === 'under_review' && !item.assigned_to && (
              <div className="flex gap-1 mt-2">
                <button onClick={(e) => { e.preventDefault(); onAssign(item.id, 'Pharmacy Committee', 'high'); }}
                  className="flex-1 text-[10px] py-1 px-1.5 bg-violet-50 text-violet-600 rounded hover:bg-violet-100 flex items-center justify-center gap-1" data-testid={`assign-committee-${item.id}`}>
                  <Users className="w-3 h-3" /> Committee
                </button>
                <button onClick={(e) => { e.preventDefault(); onAssign(item.id, 'Procurement', 'medium'); }}
                  className="flex-1 text-[10px] py-1 px-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 flex items-center justify-center gap-1" data-testid={`assign-procurement-${item.id}`}>
                  <Briefcase className="w-3 h-3" /> Procurement
                </button>
              </div>
            )}
            {stage.id === 'opportunity' && (
              <div className="flex gap-1 mt-2">
                <button onClick={(e) => { e.preventDefault(); onAssign(item.id, null, 'high'); }}
                  className="flex-1 text-[10px] py-1 px-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 flex items-center justify-center gap-1" data-testid={`priority-high-${item.id}`}>
                  <Flag className="w-3 h-3" /> High Priority
                </button>
              </div>
            )}

            {/* Move buttons */}
            <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {STAGES.findIndex(s => s.id === stage.id) > 0 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    const prevStage = STAGES[STAGES.findIndex(s => s.id === stage.id) - 1];
                    onMove(item.id, prevStage.id);
                  }}
                  className="flex-1 text-xs py-1 px-2 bg-slate-100 hover:bg-slate-200 rounded text-slate-600"
                >
                  Back
                </button>
              )}
              {STAGES.findIndex(s => s.id === stage.id) < STAGES.length - 1 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    const nextStage = STAGES[STAGES.findIndex(s => s.id === stage.id) + 1];
                    onMove(item.id, nextStage.id);
                  }}
                  className="flex-1 text-xs py-1 px-2 bg-[#0A2540] hover:bg-[#1e3a5f] rounded text-white"
                >
                  Advance
                </button>
              )}
            </div>
          </div>
        ))}
        
        {items.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            No items in this stage
          </div>
        )}
      </div>
    </div>
  );
}

export default function PipelinePage() {
  const [overview, setOverview] = useState(null);
  const [stageItems, setStageItems] = useState({});
  const [loading, setLoading] = useState(true);

  async function fetchData() {
    try {
      const overviewRes = await getPipelineOverview();
      setOverview(overviewRes.data);
      
      // Fetch all stages in parallel for faster loading
      const stagePromises = STAGES.map(stage => 
        getPipelineStage(stage.id).then(res => ({ stage: stage.id, data: res.data.generics || [] }))
      );
      const results = await Promise.all(stagePromises);
      const items = {};
      results.forEach(r => { items[r.stage] = r.data; });
      setStageItems(items);
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const handleMove = async (genericId, newStage) => {
    try {
      await movePipeline(genericId, newStage);
      toast.success(`Moved to ${STAGES.find(s => s.id === newStage)?.label}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to move item');
    }
  };

  const handleAssign = async (genericId, assignedTo, priority) => {
    try {
      await assignPipelineItem(genericId, assignedTo, priority);
      toast.success(`Assigned to ${assignedTo || 'updated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="pipeline-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="flex gap-4">
          {STAGES.map(s => (
            <div key={s.id} className="flex-1 min-w-[250px]">
              <div className="skeleton h-20 rounded-t-xl" />
              <div className="skeleton h-[400px] rounded-b-xl mt-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Execution Pipeline</h1>
          <p className="text-slate-500">Track molecules from opportunity to realized impact</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-slate-500">Total Pipeline Value</p>
            <p className="text-xl font-bold text-emerald-600">
              {formatCurrency(Object.values(overview?.values || {}).reduce((a, b) => a + b, 0))}
            </p>
          </div>
        </div>
      </div>

      {/* Pipeline Flow Indicator */}
      <div className="flex items-center justify-between mb-6 px-8">
        {STAGES.map((stage, idx) => (
          <React.Fragment key={stage.id}>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full ${stage.color} flex items-center justify-center`}>
                <stage.icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-slate-700">{stage.label}</span>
            </div>
            {idx < STAGES.length - 1 && (
              <ArrowRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Pipeline Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map(stage => (
          <StageColumn
            key={stage.id}
            stage={stage}
            items={stageItems[stage.id] || []}
            counts={overview?.counts?.[stage.id]}
            values={overview?.values?.[stage.id]}
            onMove={handleMove}
            onAssign={handleAssign}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center gap-6 text-sm text-slate-500">
        <span>Hover over items to move between stages</span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>High confidence (≥85%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span>Moderate (70-84%)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>Low (&lt;70%)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
